import { useState, useEffect, useCallback } from 'react';
import * as api from '../api/api';

/**
 * Hook para gestionar la carga de agentes por supervisor y rango de fechas
 * @param {Object} dateRange - Rango de fechas con startDate y endDate
 * @param {string|null} selectedSupervisor - Supervisor seleccionado
 * @param {Function} t - Función de traducción
 * @returns {Object} { agents, agentsEnabled, isLoading, error, getAgentsPlaceholder }
 */
export function useAgents(dateRange, selectedSupervisor, t) {
  const [agents, setAgents] = useState([]);
  const [agentsEnabled, setAgentsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Texto dinámico para el placeholder del multiselect de agentes
  const getAgentsPlaceholder = useCallback(() => {
    // Esta función se actualizará cuando cambien los agentes seleccionados
    // Se debe pasar selectedAgents como parámetro cuando se use
    return t("insights.all");
  }, [t]);

  useEffect(() => {
    let abortController = new AbortController();
    
    const loadAgents = async () => {
      if (!dateRange?.startDate || !dateRange?.endDate) {
        setAgents([]);
        setAgentsEnabled(false);
        setIsLoading(false);
        setError(null);
        return;
      }
      
      // Solo cargar agentes si HAY supervisor seleccionado
      if (!selectedSupervisor) {
        setAgents([]);
        setAgentsEnabled(false);
        setIsLoading(false);
        setError(null);
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        setAgentsEnabled(false);
        
        // Llamar API con UN supervisor
        const agentsRes = await api.getAgentsBySupervisorAndDateRange(
          selectedSupervisor, 
          dateRange.startDate, 
          dateRange.endDate
        );
        
        if (abortController.signal.aborted) return;
        
        const uniqueAgents = [...new Set(agentsRes.items || [])].filter(a => a?.trim());
        const formattedAgents = uniqueAgents.sort().map(a => ({ value: a, label: a }));
        
        setAgents(formattedAgents);
        setAgentsEnabled(true);
        
        console.log(`Agentes cargados para supervisor ${selectedSupervisor}: ${uniqueAgents.length}`);
      } catch (err) {
        console.error('Error loading agents:', err);
        setAgents([]);
        setAgentsEnabled(false);
        setError(err);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };
    
    loadAgents();
    return () => abortController.abort();
  }, [dateRange, selectedSupervisor]);

  // Función mejorada para obtener placeholder con agentes seleccionados
  const getAgentsPlaceholderWithSelection = useCallback((selectedAgents) => {
    if (!selectedAgents || selectedAgents.length === 0) {
      return t("insights.all");
    }

    const selectedLabels = agents
      .filter(opt => selectedAgents.includes(opt.value))
      .map(opt => opt.label || opt.value);

    if (selectedLabels.length === 1) {
      return selectedLabels[0];
    }

    if (selectedLabels.length <= 3) {
      return selectedLabels.join(", ");
    }

    // Muchas selecciones: mostrar solo algunas y agregar elipsis
    return `${selectedLabels.slice(0, 3).join(", ")}...`;
  }, [agents, t]);

  return {
    agents,
    agentsEnabled,
    isLoading,
    error,
    getAgentsPlaceholder: getAgentsPlaceholderWithSelection
  };
}

